import { hasPermission } from '../permissions/usePermissions';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography, Card, Alert } from 'antd';
import { useSelector } from 'react-redux';

import BulkUploadSection from '../components/admin/BulkUploadSection';
import BulkUploadFournisseurs from '../components/four/BulkUploadFournisseur';
import BulkUploadAvoirs from '../components/avoir/BulkUploadAvoirs';
import BulkUploadFactures from '../components/facture/BulkUploadFactures';
import BulkUploadBonliv from '../components/bonliv/BulkUploadBonliv';
import BulkUploadBonReception from '../components/achat/reception/BulkUploadBonReception';
import BulkUploadDemarque from '../components/avoir/BulkUploadDemarque';
import CounterManagement from '../components/admin/CounterManagement';
import Debugging from '../components/admin/Debugging'; // Import the new Debugging component
import ExportFacture from '../components/facture/ExportFacture';
import ExportExpedition from '../components/exped/ExportJournalExp'; // Import the ExportExpedition component
import ImportDevis from '../components/devis/ImportDevis';
import DownloadTables from '../components/admin/DownloadTables'; // Import the new DownloadTables component
import DownloadAchatTables from '../components/admin/DownloadAchatTables';
import ArchiveManager from '../components/archive/ArchiveManager';

// Import your hooks
import { useBulkUploadComercials } from '../hooks/comHooks';
import { useBulkUploadFamilles } from '../hooks/familleHooks';
import { useBulkUploadSousFamilles } from '../hooks/sousFamilleHooks';
import { useBulkUploadFournisseurs } from '../hooks/fourHooks';
import { useBulkUploadArticles } from '../hooks/articleHooks';
import { useBulkUploadClients } from '../hooks/clientHooks';

const { Title } = Typography;

const BulkUploadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  
  // Get upload type from URL query parameter
  const searchParams = new URLSearchParams(location.search);
  const uploadType = searchParams.get('type');

  // Initialize hooks
  const comercialUpload = useBulkUploadComercials();
  const familleUpload = useBulkUploadFamilles();
  const sousFamilleUpload = useBulkUploadSousFamilles();
  const fournisseurUpload = useBulkUploadFournisseurs();
  const articleUpload = useBulkUploadArticles();
  const clientUpload = useBulkUploadClients();

  const checkPermission = (permission) => {
    return user?.[permission] || user?.IsManager;
  };

  // Redirect if no upload type specified
  useEffect(() => {
    if (!uploadType) {
      // Default to first option if no type specified
      navigate('/bulk?type=commercials');
    }
  }, [uploadType, navigate]);

  // Check permission for current upload type
  const checkUploadPermission = () => {
    if (uploadType === 'exportdbf' || uploadType === 'importdevis' || 
        uploadType === 'exportexpedition' || uploadType === 'downloadtables' || uploadType === 'downloadachattables') {
      return true; // Anyone can access these
    }
    if (uploadType === 'counter') {
      return hasPermission(user, 'counters') || hasPermission(user, 'returnCounters');
    }
    if (uploadType === 'debugging') return ['tree', 'counters', 'returnCounters'].some(module => hasPermission(user, module));
    if (uploadType === 'archive') return hasPermission(user, 'archives');
    return checkPermission('IsManager');
  };

  // Handle unauthorized access
  if (!checkUploadPermission()) {
    return (
      <Alert
        message="Unauthorized Access"
        description="You don't have permission to access this resource."
        type="error"
        showIcon
      />
    );
  }

  // Get title based on upload type
  const getTitle = () => {
    switch (uploadType) {
      case 'commercials':
        return 'Bulk Upload Commercials';
      case 'families':
        return 'Bulk Upload Families';
      case 'subfamilies':
        return 'Bulk Upload Subfamilies';
      case 'fournisseurs':
        return 'Bulk Upload Fournisseurs';
      case 'articles':
        return 'Bulk Upload Articles';
      case 'clients':
        return 'Bulk Upload Clients';
      case 'counter':
        return 'Counter Management';
      case 'debugging': // Add title for debugging page
        return 'Debugging';
      case 'importdevis':
        return 'Import Devis';
      case 'exportdbf':
        return 'Export';
      case 'exportexpedition':
        return 'Export Journal Expédition';
      case 'downloadtables': // Add title for download tables
        return 'Download Tables (DBF)';
      case 'downloadachattables':
        return 'Download Achat Tables (DBF)';
      case 'avoirs':
        return 'Upload Avoirs DBF Files';
      case 'factures':
        return 'Upload Factures DBF Files';
      case 'bonliv':
        return 'Upload Bonliv DBF Files';
      case 'achats':
        return 'Upload BonReception DBF Files';
      case 'demarque':
        return 'Upload Demarque DBF Files';
      case 'archive':
        return 'Archive Hot Records';
      default:
        return 'Bulk Upload Management';
    }
  };

  // Render content based on upload type
  const renderContent = () => {
    if (!uploadType) return null;

    switch (uploadType) {
      case 'commercials':
        return (
          <BulkUploadSection
            title="Upload Commercial Data"
            handleBulkUpload={comercialUpload.handleBulkUpload}
            loading={comercialUpload.loading}
            error={comercialUpload.error}
            uploadResult={comercialUpload.uploadResult}
          />
        );
      case 'families':
        return (
          <BulkUploadSection
            title="Upload Family Data"
            handleBulkUpload={familleUpload.handleBulkUpload}
            loading={familleUpload.loading}
            error={familleUpload.error}
            uploadResult={familleUpload.uploadResult}
          />
        );
      case 'subfamilies':
        return (
          <BulkUploadSection
            title="Upload Subfamily Data"
            handleBulkUpload={sousFamilleUpload.handleBulkUpload}
            loading={sousFamilleUpload.loading}
            error={sousFamilleUpload.error}
            uploadResult={sousFamilleUpload.uploadResult}
          />
        );
      case 'fournisseurs':
        return <BulkUploadFournisseurs />;
      case 'articles':
        return (
          <BulkUploadSection
            title="Upload Article Data"
            handleBulkUpload={articleUpload.handleBulkUpload}
            loading={articleUpload.loading}
            error={articleUpload.error}
            uploadResult={articleUpload.uploadResult}
          />
        );
      case 'clients':
        return (
          <BulkUploadSection
            title="Upload Client Data"
            handleBulkUpload={clientUpload.handleBulkUpload}
            loading={clientUpload.loading}
            error={clientUpload.error}
            uploadResult={clientUpload.uploadResult}
          />
        );
      case 'counter':
        return <CounterManagement />;
      case 'debugging': // Add case for the debugging component
        return <Debugging />;
      case 'exportdbf':
        return <ExportFacture />;
      case 'exportexpedition':
        return <ExportExpedition />;
      case 'importdevis':
        return <ImportDevis />;
      case 'downloadtables': // Add case for the download tables component
        return <DownloadTables />;
      case 'downloadachattables':
        return <DownloadAchatTables />;
      case 'avoirs':
        return <BulkUploadAvoirs />;
      case 'factures':
        return <BulkUploadFactures />;
      case 'bonliv':
        return <BulkUploadBonliv />;
      case 'achats':
        return <BulkUploadBonReception />;
      case 'demarque':
        return <BulkUploadDemarque />;
      case 'archive':
        return <ArchiveManager />;
      default:
        return (
          <Alert
            message="Invalid Upload Type"
            description="Please select a valid upload type from the sidebar menu."
            type="warning"
            showIcon
          />
        );
    }
  };

  return (
    <div className="bulk-upload-page">
      <Title level={2}>{getTitle()}</Title>
      <Card bordered={false}>
        {renderContent()}
      </Card>
    </div>
  );
};

export default BulkUploadPage;
